import { useAuth } from '../hooks/useAuth';
import { BookOpen, Download } from 'lucide-react';

const GUIDES = {
  student: {
    title: 'Student Guide',
    description: 'Everything you need to know to apply, get sponsored, and showcase your innovation at OAU.',
    filename: 'futurum_student_guide.pdf',
  },
  lecturer: {
    title: 'Lecturer / Supervisor Guide',
    description: 'Your research topics, your students, your legacy. A guide to participating in Futurum Season II.',
    filename: 'futurum_lecturer_guide.pdf',
  },
  alumni: {
    title: 'Alumni Sponsor Guide',
    description: 'Invest in the next generation of Nigerian innovators. Your guide to sponsoring a Futurum project.',
    filename: 'futurum_sponsor_guide.pdf',
  },
  judge: {
    title: 'Judge Guide',
    description: 'Your expertise shapes the future. A guide to judging at Futurum Season II.',
    filename: 'futurum_judge_guide.pdf',
  },
  admin: {
    title: 'Platform Guides',
    description: 'All user guides are available below.',
    filename: null,
  },
};

const ALL_GUIDES = [
  { role: 'student',  title: 'Student Guide',               filename: 'futurum_student_guide.pdf' },
  { role: 'lecturer', title: 'Lecturer / Supervisor Guide',  filename: 'futurum_lecturer_guide.pdf' },
  { role: 'alumni',   title: 'Alumni Sponsor Guide',         filename: 'futurum_sponsor_guide.pdf' },
  { role: 'judge',    title: 'Judge Guide',                  filename: 'futurum_judge_guide.pdf' },
];

function GuideCard({ title, description, filename }) {
  const url = `${window.location.origin}/guides/${filename}`;
  return (
    <div style={{
      background: 'var(--ink-mid)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 12,
      padding: '28px 32px',
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 10,
          background: 'rgba(201,168,76,0.12)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <BookOpen size={20} color="var(--gold)" />
        </div>
        <div>
          <div style={{
            fontFamily: 'var(--font-display)',
            fontSize: '1.05rem',
            fontWeight: 700,
            color: 'var(--text-primary)',
            marginBottom: 2,
          }}>
            {title}
          </div>
          {description && (
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>
              {description}
            </div>
          )}
        </div>
      </div>
      <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />
      <div style={{ display: 'flex', gap: 10 }}>
        <a href={url} download={filename} style={{
          display: 'flex', alignItems: 'center', gap: 7,
          padding: '9px 18px',
          background: 'rgba(255,255,255,0.06)',
          color: 'var(--text-primary)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 7,
          fontWeight: 600,
          fontSize: '0.85rem',
          textDecoration: 'none',
        }}>
          <Download size={14} />
          Download
        </a>
      </div>
    </div>
  );
}

export default function GuidePage() {
  const { user } = useAuth();
  const role = user?.role;
  const guide = GUIDES[role];
  if (!guide) return null;
  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '32px 0' }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{
          fontFamily: 'var(--font-display)',
          fontSize: '1.6rem',
          fontWeight: 700,
          color: 'var(--text-primary)',
          marginBottom: 6,
        }}>
          User Guide
        </h1>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
          {role === 'admin'
            ? 'All platform guides are available here for reference.'
            : 'Your platform guide for Futurum Innovation Awards Season II.'}
        </p>
      </div>
      {role === 'admin' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {ALL_GUIDES.map(g => (
            <GuideCard key={g.role} title={g.title} filename={g.filename} />
          ))}
        </div>
      ) : (
        <GuideCard title={guide.title} description={guide.description} filename={guide.filename} />
      )}
      <p style={{ marginTop: 28, fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
        If you have questions not covered in this guide, contact{' '}
        <a href="mailto:admin@futurumawards.com" style={{ color: 'var(--teal)', textDecoration: 'none' }}>
          admin@futurumawards.com
        </a>.
      </p>
    </div>
  );
}
